import * as _ from 'lodash';
import { IMapLayerProvider } from 'app/config/IMapLayerProvider';
import { generateUUID } from 'app/shared/uuid.service';

export type FileParameters = { [key: string]: any };
/**
 * This is the data structure that gets saved to the database
 */
interface JSFeatureProvider {
    /**
     * Name given to layer. This is the name that will be appearing in the map layers
     */
    name: string;
    /**
     * Unique id for the layer
     */
    uuid: string;
    /**
     * The type of file we are using for the layer
     */
    fileType: ImsFeatureProviderType;
    /**
     * Where the file is being loaded from. File from the api or url
     */
    loadingFrom: ImsFeatureProviderLoadingFromType;
    /**
     * The url the file used for this layer. Must be populated when loadingFrom is url
     */
    url?: string;
    /**
     * The file used for this layer. Must be populated when loadingFrom is file.
     * Example:
     * {
     *   "fileData": "UEsDBBQAAAAIACVAb1S+YV" // base64 format
     *   "lastModified": 1651552401686,
         "name": "AHA-PMP-BS.KMZ",
         "size": 99657,
         "type": ""
     * }
     */
    file?: FileParameters;
    /**
     * Hide or display layer in map layers
     */
    hideInMapLayers: boolean;
    /**
     * Show of hide the layer by default
     */
    showByDefault: boolean;
}

export enum ImsFeatureProviderType {
    KML = 'KML',
    KMZ = 'KMZ',
}

export enum ImsFeatureProviderLoadingFromType {
    FILE = 'file',
    URL = 'url',
}

export class ImsFeatureLayerProvider implements IMapLayerProvider {
    private _name: string;
    private readonly _uuid: string;
    private _fileType: ImsFeatureProviderType;
    private _loadingFrom: ImsFeatureProviderLoadingFromType;
    private _url: string;
    private _file: FileParameters;
    private _hideInMapLayers: boolean;
    private _showByDefault: boolean;
    /**
     *
     * @param {JSFeatureProvider} obj plain old javascript object used to create
     * this provider
     */
    constructor(obj: JSFeatureProvider) {
        if (!obj) {
            obj = {} as any;
        }

        obj = Object.assign(
            {},
            {
                name: 'New Feature Layer',
                uuid: generateUUID(),
                loadingFrom: ImsFeatureProviderLoadingFromType.FILE,
            },
            obj,
        );

        this._name = obj.name;
        this._uuid = obj.uuid;
        this._fileType = obj.fileType;
        this._loadingFrom = obj.loadingFrom;
        this._file = obj.file;
        this._url = obj.url;
        this._showByDefault = obj.showByDefault;
        this._hideInMapLayers = !!obj.hideInMapLayers;
    }

    get name(): string {
        return this._name;
    }

    set name(name: string) {
        this._name = name;
    }

    get url(): string {
        return this._url;
    }

    set url(val: string) {
        this._url = val;
    }

    get file(): FileParameters {
        return this._file;
    }

    set file(val: FileParameters) {
        this._file = val;
    }

    get uuid(): string {
        return this._uuid;
    }

    get fileType(): ImsFeatureProviderType {
        return this._fileType;
    }

    set fileType(val: ImsFeatureProviderType) {
        this._fileType = val;
    }

    get loadingFrom(): ImsFeatureProviderLoadingFromType {
        return this._loadingFrom;
    }

    set loadingFrom(val: ImsFeatureProviderLoadingFromType) {
        this._loadingFrom = val;
    }

    get hideInMapLayers(): boolean {
        return this._hideInMapLayers;
    }

    set hideInMapLayers(value: boolean) {
        this._hideInMapLayers = !!value;
    }

    get showByDefault(): boolean {
        return this._showByDefault;
    }

    set showByDefault(value: boolean) {
        this._showByDefault = value;
    }

    toJS(): JSFeatureProvider {
        return {
            name: this._name,
            uuid: this._uuid,
            fileType: this._fileType,
            loadingFrom: this._loadingFrom,
            url: this._url,
            file: this._file,
            hideInMapLayers: this._hideInMapLayers,
            showByDefault: this._showByDefault,
        };
    }

    clone(): this {
        return new ImsFeatureLayerProvider(this.toJS()) as any;
    }

    isDiff(other: this): boolean {
        // How is this for a short cut!
        return !_.isEqual(this.toJS(), other.toJS());
    }
}
